<script setup lang="ts">
import { readItems } from '@directus/sdk';

import type { Package } from '~/types';
definePageMeta({
  auth: false,
})

const { locale: current_locale, t } = useI18n({
  useScope: 'local',
})

const { request } = useDirectusRest()

const { data, reset, steps } = useMultiStepForm()

onMounted(() => {
  reset()
})

const { data: packages } = await useAsyncData(
  'packages',
  () =>
    request<Package[]>(
      readItems('package', {
        deep: {
          available_drinks: {
            drink_id: {
              translations: {
                _filter: {
                  languages_code: {
                    _eq: current_locale.value,
                  },
                },
              },
            },
          },
          default_drink: {
            translations: {
              _filter: {
                languages_code: {
                  _eq: current_locale.value,
                },
              },
            },
          },
          translations: {
            _filter: {
              languages_code: {
                _eq: current_locale.value,
              },
            },
          },
        },
        fields: [
          'id',
          'picture',
          'price',
          'bob_by_feestfiets',
          'locked',
          'bob_price',
          'default_bob',
          {
            available_drinks: ['drink_id'],
            available_durations: [
              {
                duration_id: ['id', 'duration', 'price'],
              },
            ],
            default_delivery_fee: ['id', 'price'],
            default_drink: ['id', 'price', { translations: ['name'] }],
            default_duration: ['id', 'price', 'duration'],
            location: [
              'id',
              'title',
              'delivery_street',
              'delivery_number',
              'delivery_postal_code',
              'delivery_city',
              'backup_delivery_street',
              'backup_delivery_number',
              'backup_delivery_postal_code',
              'backup_delivery_city',
              'pick_up_street',
              'pick_up_number',
              'pick_up_postal_code',
              'pick_up_city',
              'backup_pick_up_street',
              'backup_pick_up_number',
              'backup_pick_up_postal_code',
              'backup_pick_up_city',
              'duration',
            ],
            translations: ['name', 'languages_code', 'description', 'weight'],
          },
        ],
        filter: {
          agency_only: false,
          status: 'published',
        },
        sort: ['translations.weight'],
      }),
    ),
  {
    watch: [current_locale],
  },
)

const { data: content } = await useAsyncData(
  () =>
    request(
      readSingleton('content', {
        deep: {
          translations: {
            _filter: {
              languages_code: {
                _eq: current_locale.value,
              },
            },
          },
        },
        fields: [{ translations: ['packages_subtitle'] }],
      }),
    ),
  {
    transform: (result) => result?.translations[0],
    watch: [current_locale],
  },
)

const visibleSteps = computed(() => {
  const packageData = data.value.package

  return steps.value.slice(1, -1).filter((step) => {
    if ('condition' in step) return step.condition(packageData)

    return true
  })
})

const getNextVisibleStep = (index: number) => {
  return visibleSteps.value.find((step) => Number(step.id) > index)
}

const onPackageSelect = (item) => {
  reset()
  data.value.package = item
  goToNextStep()
}

const goToNextStep = async () => {
  const { package: packageItem } = data.value

  Object.assign(data.value, {
    bob_by_feestfiets:
      packageItem?.default_bob || packageItem.bob_by_feestfiets,
    delivery_fee: packageItem?.default_delivery_fee,
    drink: packageItem?.default_drink,
    duration: packageItem?.default_duration?.id,
  })

  if (packageItem.location) {
    const { location } = packageItem
    Object.assign(data.value, {
      collection_city: location.pick_up_city,
      collection_number: location.pick_up_number,
      collection_postal_code: location.pick_up_postal_code,
      collection_street: location.pick_up_street,
      delivery_city: location.delivery_city,
      delivery_number: location.delivery_number,
      delivery_postal_code: location.delivery_postal_code,
      delivery_street: location.delivery_street,
    })
  }

  // if (packageItem.location) {
  //   const { location } = packageItem
  //   let deliveryFee
  //   if (!packageItem.locked) {
  //     const [response] = await request(
  //       readItems('postal_code', {
  //         fields: ['zone.*'],
  //         filter: {
  //           code: location.delivery_postal_code,
  //         },
  //       }),
  //     )
  //     deliveryFee = response?.zone
  //   }
  //   Object.assign(data.value, {
  //     collection_city: location.pick_up_city,
  //     collection_number: location.pick_up_number,
  //     collection_postal_code: location.pick_up_postal_code,
  //     collection_street: location.pick_up_street,
  //     delivery_city: location.delivery_city,
  //     delivery_fee: deliveryFee,
  //     delivery_number: location.delivery_number,
  //     delivery_postal_code: location.delivery_postal_code,
  //     delivery_street: location.delivery_street,
  //   })
  // }

  // if (data.value.package.available_durations?.length) {
  //   data.value.duration =
  //     data.value.package.available_durations[0]?.duration_id?.id
  // }

  navigateTo(getNextVisibleStep(0)?.to)
}
</script>

<template>
  <div>
    <div class="grid grid-cols-[1fr,auto,1fr] mt-20">
      <div class="items-end justify-end hidden [@media(min-width:1370px)]:flex">
        <NuxtImg class="  " alt="" :src="`/img/feestfiets.svg`" width="375" />
      </div>
      <div class="relative col-start-2 ">
        <CustomerHero class="z-10" />
      </div>
    </div>
    <BookingFormContainer size="full">
      <div
        class="col-span-12 grid gap-y-8 px-5 grid-cols-1 md:grid-cols-[repeat(2,1fr)_auto_1fr] md:gap-4 xl:grid-cols-[repeat(4,1fr)_auto_1fr] xl:px-0">
        <div class="">
          <h2 class="text-end text-2xl font-bold">{{ t('fixed_packages') }}</h2>
        </div>
        <div class="hidden md:block xl:col-span-3"></div>
        <div class="row-span-2 hidden h-full w-1.5 flex-col gap-2 md:col-start-3 md:flex xl:col-start-5">
          <svg class="h-full w-full">
            <line stroke="black" stroke-dasharray="10 10" stroke-linecap="round" stroke-width="5" x1="3" x2="3" y1="0"
              y2="100%" />
          </svg>
        </div>
        <div :style="{
          '--row-start': packages?.length + 1,
        }" class="row-start-[--row-start] md:col-start-4 md:row-start-1 xl:col-start-6">
          <h2 class="text-2xl font-bold">{{ t('custom') }}</h2>
        </div>
        <div :key="item.id" class="lg:pb-10" v-for="item in packages
          ?.filter((item) => item.location)
          .sort((a, b) => {
            return a.translations[0].weight - b.translations[0].weight
          })">
          <PackageCard :item="item" @selected="onPackageSelect(item)" />
        </div>
        <div :key="item.id" class="lg:pb-10 md:row-start-2 col-end-[-1] "
          v-for="item in packages?.filter((item) => !item.location)">
          <PackageCard :item="item" @selected="onPackageSelect(item)" />
        </div>
      </div>
      <div class="col-span-12 mt-4">
        <p class="text-center text-2xl font-bold">
          {{ content?.packages_subtitle }}
        </p>
      </div>
    </BookingFormContainer>
    <CustomerFeatures />
  </div>
</template>

<i18n lang="json">{
  "nl": {
    "fixed_packages": "Vaste pakketten",
    "custom": "Zelf samenstellen",
    "packages_subtitle": "Je kan alles nog volledig samenstellen en aanpassen naar wens"
  },
  "en": {
    "fixed_packages": "Fixed packages",
    "custom": "Assemble yourself",
    "packages_subtitle": "You can still fully customize and adjust everything to your liking"
  },
  "fr": {
    "fixed_packages": "Forfaits fixes",
    "custom": "Assemblez vous-même",
    "packages_subtitle": "Vous pouvez toujours tout personnaliser et ajuster à votre goût"
  }
}</i18n>
